import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listAssignableExternalMembers,
  listProjectExternalMembers,
  removeProjectExternalMembers,
  type AssignableExternalMember,
  type ProjectExternalMember,
  type RemoveProjectExternalMembersResult,
} from '../services/api/externalInvites';

export function useProjectExternalMembers(projectId: number, enabled = true) {
  return useQuery<ProjectExternalMember[], Error>({
    queryKey: ['project-external-members', projectId],
    queryFn: () => listProjectExternalMembers(projectId),
    enabled: enabled && Number.isFinite(projectId) && projectId > 0,
    staleTime: 10_000,
  });
}

/**
 * MKS-513 — the assignee picker's External tab.
 *
 * A separate query key from `useProjectExternalMembers`: the two hit
 * different endpoints with different audiences and different payloads, so
 * they must not share a cache entry.
 */
export function useAssignableExternalMembers(projectId: number, enabled = true) {
  return useQuery<AssignableExternalMember[], Error>({
    queryKey: ['project-assignable-external-members', projectId],
    queryFn: () => listAssignableExternalMembers(projectId),
    enabled: enabled && Number.isFinite(projectId) && projectId > 0,
    staleTime: 10_000,
  });
}

export interface RemoveExternalMembersInput {
  projectId: number;
  externalUserIds: number[];
}

export function useRemoveProjectExternalMembers() {
  const queryClient = useQueryClient();
  return useMutation<RemoveProjectExternalMembersResult, Error, RemoveExternalMembersInput>({
    mutationFn: ({ projectId, externalUserIds }) =>
      removeProjectExternalMembers(projectId, externalUserIds),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['project-external-members', vars.projectId],
      });
    },
  });
}
